import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ShieldCheck,
    FileCheck,
    Banknote,
    Scale,
    Ban,
    Lock,
    Landmark,
    AlertTriangle,
    Gavel,
    CheckCircle2,
    Package,
} from "lucide-react";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const sections = [
    {
        id: "defence-compliance",
        icon: ShieldCheck,
        title: "Defence Compliance",
        intro:
            "Indravax provides advisory services in relation to defence procurement and strategic systems. The firm does not manufacture, trade, or directly supply defence equipment.",
        body: "All engagements in the defence sector are conducted in compliance with:",
        points: [
            "Applicable export control laws and regulations",
            "End-user certification (EUC) requirements",
            "Government approvals and licensing frameworks",
            "International compliance standards governing defence transactions",
        ],
        footer: "Indravax only participates in engagements where all parties demonstrate adherence to applicable legal and regulatory requirements.",
    },
    {
        id: "export-control",
        icon: FileCheck,
        title: "Export Control & Regulatory Adherence",
        intro: "All transactions involving controlled goods, dual-use items, or regulated technologies are subject to:",
        points: [
            "Export licensing requirements of the relevant jurisdictions",
            "Compliance with international trade control regimes",
            "Verification of authorized end-use and end-user",
        ],
        footer: "The firm does not engage in any transaction that violates export control regulations or sanctions frameworks.",
    },
    {
        id: "trade-finance-compliance",
        icon: Banknote,
        title: "Trade Finance & Financial Compliance",
        intro:
            "Indravax provides advisory on structured financial instruments including SBLCs, Letters of Credit, and Bank Guarantees.",
        body: "All such advisory is conducted in alignment with:",
        points: [
            "International banking standards",
            "Anti-Money Laundering (AML) regulations",
            "Know Your Customer (KYC) requirements",
            "Applicable financial compliance frameworks",
        ],
        footer: "The firm does not act as a financial institution and does not hold or transfer client funds.",
    },
    {
        id: "international-trade-compliance",
        icon: Package,
        title: "International Trade & Commercial Facilitation",
        intro:
            "Indravax Strategic Advisory LLP provides advisory and facilitation services in relation to international trade and does not act as a principal buyer or seller unless specifically authorized under applicable laws and agreements.",
        body: "All international trade engagements are structured in alignment with:",
        points: [
            "Applicable import-export and trade control regulations",
            "Incoterms and recognised commercial frameworks",
            "Documentation standards (LOI, FCO, SPA, LC, SBLC, BG)",
            "AML, KYC, and counterparty due-diligence requirements",
        ],
        footer: "The firm operates as advisor and facilitator — coordinating between counterparties and aligning commercial, logistical, and financial frameworks.",
    },
    {
        id: "anti-corruption",
        icon: Scale,
        title: "Anti-Corruption & Ethical Conduct",
        intro: "Indravax maintains a zero-tolerance policy toward:",
        points: ["Corruption", "Bribery", "Undisclosed commissions", "Unlawful inducements"],
        footer: "All engagements are expected to comply with applicable anti-corruption laws and ethical standards across jurisdictions.",
    },
    {
        id: "sanctions",
        icon: Ban,
        title: "Sanctions Compliance",
        intro: "The firm does not engage in transactions involving:",
        points: [
            "Sanctioned countries, entities, or individuals",
            "Restricted or prohibited goods and services",
            "Transactions that may violate international sanctions regimes",
        ],
        footer: "All counterparties are expected to undergo appropriate due diligence.",
    },
    {
        id: "confidentiality",
        icon: Lock,
        title: "Confidentiality",
        intro:
            "All client engagements are conducted under strict confidentiality protocols. Information shared with Indravax is handled with discretion and used solely for the purpose of advisory and transaction execution.",
    },
    {
        id: "independent-advisory",
        icon: Landmark,
        title: "Independent Advisory Status",
        intro: "Indravax Strategic Advisory LLP acts solely as an independent advisory firm. The firm:",
        points: [
            "Does not act as a principal buyer or seller",
            "Does not take ownership of goods",
            "Does not provide financial guarantees",
            "Does not act on behalf of governments unless formally mandated",
        ],
    },
    {
        id: "no-affiliation",
        icon: AlertTriangle,
        title: "No Affiliation Disclaimer",
        intro:
            "Indravax Strategic Advisory LLP is an independent entity and is not affiliated with, endorsed by, or acting on behalf of any government, defence organization, multilateral institution, or international agency unless explicitly stated in a formal written agreement.",
    },
    {
        id: "limitation-of-liability",
        icon: Gavel,
        title: "Limitation of Liability",
        intro: "All advisory services are provided based on information available at the time of engagement. Indravax shall not be held liable for:",
        points: [
            "Actions taken by third parties",
            "Regulatory changes affecting transactions",
            "Delays or failures beyond its control",
        ],
        footer: "Clients are advised to seek independent legal, financial, and technical advice where necessary.",
    },
    {
        id: "compliance-commitment",
        icon: CheckCircle2,
        title: "Compliance Commitment",
        intro:
            "Indravax is committed to maintaining the highest standards of compliance, transparency, and professionalism in all engagements. The firm reserves the right to decline or withdraw from any transaction that does not meet its compliance criteria.",
    },
];

const Legal = () => {
    return (
        <div className="page-enter">
            <SEO
                title="Legal & Compliance"
                description="Indravax Strategic Advisory LLP operates within strict legal, regulatory, and ethical compliance — including defence compliance, AML/KYC, sanctions, and anti-corruption frameworks."
                path="/legal"
                keywords="indravax legal, compliance, AML KYC, sanctions compliance, defence compliance, anti-corruption"
            />
            {/* HERO */}
            <section className="relative min-h-[55vh] flex items-center overflow-hidden" data-testid="legal-hero">
                <GlobeLines className="opacity-[0.12]" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040B1A]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="inline-flex items-center gap-3 mb-10">
                            <span className="h-px w-10 bg-[#C9A14A]" />
                            <span className="overline">Legal & Regulatory</span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-7xl text-white leading-[1.02] max-w-4xl">
                            Legal & <span className="italic font-light text-[#C9A14A]">Compliance.</span>
                        </h1>
                        <p className="mt-10 text-lg text-slate-300 font-light leading-[1.85] max-w-3xl">
                            Indravax Strategic Advisory LLP operates within a strict framework of legal,
                            regulatory, and ethical compliance across all jurisdictions in which it conducts
                            business. The firm is committed to ensuring that all advisory and
                            transaction-related activities adhere to applicable national and international
                            laws, particularly in sectors involving defence, trade finance, and cross-border
                            transactions.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* TABLE OF CONTENTS + SECTIONS */}
            <section className="py-20 md:py-28 border-t border-[#C9A14A]/15" data-testid="legal-main">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* TOC */}
                    <aside className="lg:col-span-4 lg:sticky lg:top-28 h-fit" data-testid="legal-toc">
                        <Overline>Index</Overline>
                        <h3 className="font-serif text-2xl text-white mt-4 mb-8">On this page</h3>
                        <ol className="space-y-3">
                            {sections.map((s, i) => (
                                <li key={s.id} className="flex items-baseline gap-3 text-sm group">
                                    <span className="text-[0.65rem] tracking-[0.22em] text-[#C9A14A]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <a
                                        href={`#${s.id}`}
                                        className="text-slate-400 hover:text-[#C9A14A] transition-colors font-light leading-relaxed"
                                        data-testid={`legal-toc-${s.id}`}
                                    >
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </aside>

                    {/* CONTENT */}
                    <div className="lg:col-span-8 space-y-16">
                        {sections.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <FadeUp key={s.id} delay={i * 0.05}>
                                    <article
                                        id={s.id}
                                        className="scroll-mt-28 pb-14 border-b border-[#C9A14A]/15 last:border-0"
                                        data-testid={`legal-section-${s.id}`}
                                    >
                                        <div className="flex items-center gap-4 mb-6">
                                            <Icon size={28} strokeWidth={1.1} className="text-[#C9A14A]" />
                                            <div className="text-[0.65rem] tracking-[0.3em] text-slate-500 uppercase">
                                                Section {String(i + 1).padStart(2, "0")}
                                            </div>
                                        </div>
                                        <h2 className="font-serif text-3xl md:text-4xl text-white leading-[1.1]">
                                            {s.title}
                                        </h2>
                                        <p className="mt-6 text-slate-300 font-light leading-[1.9] text-base md:text-lg">
                                            {s.intro}
                                        </p>
                                        {s.body && (
                                            <p className="mt-4 text-slate-400 font-light leading-[1.9]">{s.body}</p>
                                        )}
                                        {s.points && (
                                            <ul className="mt-6 space-y-4">
                                                {s.points.map((p) => (
                                                    <li key={p} className="flex gap-4 pb-3 border-b border-[#C9A14A]/10 last:border-0">
                                                        <span className="h-px w-4 bg-[#C9A14A] mt-3 shrink-0" />
                                                        <span className="text-slate-300 font-light leading-relaxed">{p}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {s.footer && (
                                            <p className="mt-6 text-slate-400 font-light italic leading-relaxed border-l-2 border-[#C9A14A]/60 pl-5">
                                                {s.footer}
                                            </p>
                                        )}
                                    </article>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-28 border-t border-[#C9A14A]/15 bg-[#07132E]/40" data-testid="legal-cta">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <Overline>Questions on Compliance</Overline>
                    <h2 className="font-serif text-3xl md:text-5xl text-white mt-8 leading-[1.1]">
                        For compliance or regulatory enquiries, contact the firm directly.
                    </h2>
                    <p className="mt-6 text-slate-400 font-light max-w-2xl mx-auto leading-relaxed">
                        All engagements are subject to Indravax's internal compliance criteria and applicable
                        legal frameworks. The firm reserves the right to decline or withdraw from any
                        transaction at its discretion.
                    </p>
                    <Link to="/contact" className="btn-primary mt-12 inline-flex" data-testid="legal-cta-btn">
                        Contact the Firm <ArrowRight size={14} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Legal;
